import React, {useState} from "react";
import BottomMenuBar from './BottomMenuBar';
import EmailForm from './EmailForm';
import BackArrow from './BackArrow';

const education = [
  {
    title: 'MSc Management of Information Systems and Digital Innovation',
    place: 'London School of Economics and Political Science',
    period: 'Current',
    details: 'Studying how organisations adopt, govern and create value from digital technology.',
  },
  {
    title: 'B.A. Computer Science, minor in English Literature',
    place: 'Middlebury College',
    period: '2023',
  },
];

const experience = [
  {
    title: 'Consultant',
    place: 'HiQ, Stockholm',
    period: '2023 – 2026',
    details: 'Advised and delivered for clients in the infrastructure, defence and transportation sectors.',
    points: [
      'Long-term secondment to a major industrial client',
      'Led work end to end, from defining needs with external stakeholders to delivering fully implemented solutions',
      'Coordinated across teams to bring new digital systems onto large-scale platforms',
    ],
  },
];

const focusAreas = [
  'Digital Transformation',
  'Stakeholder Management',
  'Project Management',
  'Agile Ways of Working',
  'Information Systems',
  'AI & Emerging Technology',
];

function Entry({ title, place, period, details, points, isLast }) {
  return (
    <div style={{ ...styles.entry, ...(isLast ? styles.entryLast : {}) }}>
      <span style={styles.entryDot} />
      <div style={styles.entryHeader}>
        <h3 style={styles.entryTitle}>{title}</h3>
        <span style={styles.entryPeriod}>{period}</span>
      </div>
      <p style={styles.entryPlace}>{place}</p>
      {details && <p style={styles.text}>{details}</p>}
      {points && (
        <ul style={styles.points}>
          {points.map((point) => (
            <li key={point} style={styles.point}>{point}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CareerSummary() {

    const [isEmailVisible, setisEmailVisible] = useState(false);
    const toggleEmailVisibility = () => {
        setisEmailVisible(!isEmailVisible);
    };
  return (
    <div style={styles.page}>
      <BackArrow />
      <div style={styles.wrapper}>
        {/* Header */}
        <header style={styles.header}>
          <h1 style={styles.name}>Axel Schrotti</h1>
          <p style={styles.tagline}>
            MSc Management of Information Systems and Digital Innovation
          </p>
          <p style={styles.location}>London School of Economics · London, UK</p>
        </header>

        {/* Profile */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Profile</h2>
          <p style={styles.text}>
            I am passionate about steering digital change and about helping organisations turn new technology
            into something that actually works for the people using it. With a background in consulting, I thrive on
            tackling tough challenges together with smart, like-minded people. I believe that great teamwork, clear
            communication and a shared commitment to problem-solving lead to the most impactful solutions.
            Please feel free to reach out if you have questions!
          </p>
        </section>

        {/* Education */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Education</h2>
          {education.map((item, index) => (
            <Entry key={item.title} {...item} isLast={index === education.length - 1} />
          ))}
        </section>

        {/* Experience */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Experience</h2>
          {experience.map((item, index) => (
            <Entry key={item.title} {...item} isLast={index === experience.length - 1} />
          ))}
        </section>

        {/* Focus Areas */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Focus Areas</h2>
          <ul style={styles.focusGrid}>
            {focusAreas.map((area) => (
              <li key={area} style={styles.focusItem}>{area}</li>
            ))}
          </ul>
        </section>
      </div>
        {isEmailVisible && <EmailForm onClose={toggleEmailVisibility} />}
        <BottomMenuBar onShowBox={toggleEmailVisibility}/>
    </div>
  );
}

// clamp() keeps sizes comfortable on both phones and desktops
const styles = {
  page: {
    backgroundColor: '#1e3a8a',
    minHeight: '100vh',
    color: 'white',
    padding: '56px 16px 100px', // Bottom padding keeps content clear of the menu bar
    boxSizing: 'border-box',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  wrapper: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '40px',
    borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
    paddingBottom: '30px',
  },
  name: {
    fontSize: 'clamp(34px, 9vw, 48px)',
    fontWeight: '700',
    margin: '0 0 10px',
    letterSpacing: '-1px',
  },
  tagline: {
    fontSize: 'clamp(16px, 4.5vw, 20px)',
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '300',
    margin: '0 0 6px',
  },
  location: {
    fontSize: '15px',
    color: 'rgba(255, 255, 255, 0.65)',
    margin: 0,
  },
  section: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: 'clamp(20px, 5vw, 30px)',
    borderRadius: '12px',
    marginBottom: '24px',
    backdropFilter: 'blur(10px)',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    color: 'rgba(255, 255, 255, 0.65)',
    margin: '0 0 20px',
  },
  text: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: 'rgba(255, 255, 255, 0.9)',
    margin: 0,
  },
  entry: {
    position: 'relative',
    paddingLeft: '22px',
    paddingBottom: '24px',
    borderLeft: '2px solid rgba(255, 255, 255, 0.25)',
  },
  entryLast: {
    paddingBottom: 0,
  },
  entryDot: {
    position: 'absolute',
    left: '-7px',
    top: '6px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: 'white',
  },
  entryHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '4px 16px',
  },
  entryTitle: {
    fontSize: 'clamp(17px, 4.5vw, 20px)',
    fontWeight: '600',
    lineHeight: '1.3',
    margin: 0,
  },
  entryPeriod: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.65)',
    whiteSpace: 'nowrap',
  },
  entryPlace: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.8)',
    margin: '4px 0 10px',
  },
  points: {
    margin: '10px 0 0',
    paddingLeft: '20px',
  },
  point: {
    fontSize: '16px',
    lineHeight: '1.7',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  focusGrid: {
    listStyle: 'none',
    padding: '0',
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
  },
  focusItem: {
    padding: '12px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '8px',
    fontSize: '16px',
    textAlign: 'center',
  },
};
